import { zodResolver } from '@hookform/resolvers/zod';
import { Search } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

const searchSchema = z.object({
  name: z.string(),
});

type SearchFormData = z.infer<typeof searchSchema>;

interface SearchPromptVariablesFormProps {
  searchTerm: string;
  onSearchChange: (searchTerm: string) => void;
}

export function VariablesSearchForm({
  searchTerm,
  onSearchChange,
}: SearchPromptVariablesFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SearchFormData>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      name: '',
    },
  });

  const watchedName = watch('name');

  useEffect(() => {
    if (!watchedName) {
      onSearchChange('');
    }
  }, [watchedName, onSearchChange]);

  useEffect(() => {
    setValue('name', searchTerm);
  }, [searchTerm, setValue]);

  function onSubmit(data: SearchFormData) {
    onSearchChange(data.name);

    if (!data.name) {
      toast.error('Search name is required');
      return;
    }
  }

  return (
    <div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-3 sm:flex-row"
      >
        <div className="relative flex-1">
          <FormGroup error={{ message: errors.name?.message, font: 'sm' }}>
            <Input
              id="name"
              className="w-full pr-10"
              placeholder="Search prompt variables by name..."
              {...register('name')}
            />
          </FormGroup>
        </div>

        <Button type="submit" className="flex items-center gap-2">
          <Search className="h-4 w-4" />
          Search
        </Button>
      </form>
    </div>
  );
}
